import React, { useEffect, useState, forwardRef } from "react";
import { Formik } from "formik";
import { notification } from "antd";
import { InfoCircleFilled } from "@ant-design/icons";
import Button from "@/common/elements/Button";
import Checkbox from "@/common/elements/InputElements/CheckBox";
import FormItem from "@/common/elements/InputElements/FormItem.jsx";
import Select from "@/common/elements/InputElements/Select";
import TextField from "@/common/elements/InputElements/TextField";
import { StyledFlex, StyledGridCol, StyledGridRow } from "@/common/styles";
import styled from "styled-components";
import {
  getOptionsFromRefList,
  isEmptyObject,
  isNonEmptyVariable,
  objectToArray,
} from "@/util/common_function";
import { useFilterSearchContext } from "@/contexts/FolderSearchContext";
import CommonDatePicker from "@/common/elements/DatePicker";
import dayjs from "dayjs";
import useDialog from "@/hooks/useDialog";

const StyledSearchWrapper = styled.div`
  &.styled-filter-light {
    background-color: #f3f6ff;
    padding: 15px;
    border-radius: 6px;
    .ant-select-arrow {
      > .anticon {
        svg {
          fill: var(--label-color-primary);
          font-size: 14.5px;
        }
      }
    }
  }
  .dark-form-item .ant-form-item-label label {
    color: var(--text-color-blue);
  }
`;

const FolderDetailSearchForm = forwardRef(
  (
    {
      isFilterGrayBg = false,
      isDarkFormMode = false,
      initialValues: initialValuesProp = {},
    },
    ref,
  ) => {
    const { setRequestFilter } = useFilterSearchContext();
    const { showDialog } = useDialog();
    const [formData, setFormData] = useState({});
    const [api, contextHolder] = notification.useNotification();

    const openNotification = () => {
      api.open({
        message: "Warning!",
        description:
          "You did not enter enough information to do a folder search. Please enter more information.",
        duration: 0,
        className: "custom-notification",
        icon: <InfoCircleFilled style={{ color: "#0b3d5b" }} />,
      });
    };

    // Watch for form data changes and validate before calling API
    useEffect(() => {
      let filter = [];
      let restrict = false;
      if (!isEmptyObject(formData)) {
        filter = objectToArray(formData, "filterKey", "value");

        const hasFilterKey = (key) => filter.some((f) => f.filterKey === key);

        const hasValidFilter =
          (hasFilterKey("lName") && hasFilterKey("fName")) ||
          hasFilterKey("jacketId") ||
          hasFilterKey("ssn") ||
          hasFilterKey("dlNumber") ||
          hasFilterKey("dlState") ||
          hasFilterKey("pinNo") ||
          hasFilterKey("sid");

        if (hasValidFilter) {
          restrict = false;
        } else {
          restrict = true;
          openNotification();
        }
      }

      if (!restrict) {
        setRequestFilter(filter);
      }
    }, [formData, setRequestFilter]);

    useEffect(() => {
      return () => setRequestFilter([]);
    }, []);

    // Triggered on form submit
    const searchFilter = (values) => {
      if (
        !isNonEmptyVariable(values?.lName) ||
        !isNonEmptyVariable(values?.lName)
      ) {
        showDialog("info", {
          title: "Not enough info",
          content:
            "You didn't enter enough information to do a folder search. please enter more information",
          confirmButtonText: "OK",
        });
        return;
      }
      setFormData({ ...values });
    };

    // Clear committed filter once all fields are cleared
    const resetState = (values) => {
      if (isEmptyObject(values)) {
        setFormData({});
      }
    };

    return (
      <>
        {contextHolder}
        <Formik
          initialValues={
            !isEmptyObject(initialValuesProp)
              ? initialValuesProp
              : formData || {}
          }
          enableReinitialize={true}
          innerRef={ref}
          onSubmit={(values) => searchFilter(values)}
        >
          {({ getFieldProps, setFieldValue, values, resetForm }) => {
            useEffect(() => {
              resetState(values);
            }, [values]);

            return (
              <form>
                <StyledSearchWrapper
                  className={isFilterGrayBg ? "styled-filter-light" : ""}
                >
                  <StyledGridRow
                    wrap={true}
                    gutter={16}
                    className={isDarkFormMode ? "dark-form-item" : ""}
                  >
                    {/* Row 1: Folder ID & Last Name */}
                    <StyledGridCol xs={24}>
                      <StyledGridRow gutter={16}>
                        <StyledGridCol xs={3}>
                          <FormItem label="Folder ID">
                            <TextField
                              id="jacketId"
                              name="jacketId"
                              {...getFieldProps("jacketId")}
                              value={values.jacketId || ""}
                            />
                          </FormItem>
                        </StyledGridCol>
                        <StyledGridCol xs={5}>
                          <FormItem label="Last Name">
                            <TextField
                              id="lName"
                              name="lName"
                              {...getFieldProps("lName")}
                              value={values.lName || ""}
                            />
                          </FormItem>
                        </StyledGridCol>
                        <StyledGridCol xs={5}>
                          <FormItem label="First Name">
                            <TextField
                              id="fName"
                              name="fName"
                              {...getFieldProps("fName")}
                              value={values.fName || ""}
                            />
                          </FormItem>
                        </StyledGridCol>
                        <StyledGridCol xs={4}>
                          <FormItem label="Middle Name">
                            <TextField
                              id="mName"
                              name="mName"
                              {...getFieldProps("mName")}
                              value={values.mName || ""}
                            />
                          </FormItem>
                        </StyledGridCol>
                        <StyledGridCol xs={3}>
                          <FormItem label="Suffix">
                            <Select
                              id="suffix"
                              name="suffix"
                              options={getOptionsFromRefList("Suffix")}
                              placeholder="Suffix"
                              {...getFieldProps("suffix")}
                              onChange={(value) =>
                                setFieldValue("suffix", value)
                              }
                              value={values.suffix || ""}
                            />
                          </FormItem>
                        </StyledGridCol>
                        <StyledGridCol xs={4}>
                          <FormItem label="Race">
                            <Select
                              id="race"
                              name="race"
                              options={getOptionsFromRefList("Race")}
                              placeholder="Race"
                              {...getFieldProps("race")}
                              onChange={(value) => setFieldValue("race", value)}
                              value={values.race || ""}
                            />
                          </FormItem>
                        </StyledGridCol>
                      </StyledGridRow>
                    </StyledGridCol>

                    <StyledGridCol xs={24}>
                      <StyledGridRow gutter={16}>
                        <StyledGridCol xs={3}>
                          <FormItem label="Gender">
                            <Select
                              id="sex"
                              name="sex"
                              options={getOptionsFromRefList(
                                "gender",
                                false,
                                true,
                              )}
                              placeholder="Gender"
                              {...getFieldProps("sex")}
                              onChange={(value) => setFieldValue("sex", value)}
                              value={values.sex || ""}
                            />
                          </FormItem>
                        </StyledGridCol>
                        <StyledGridCol xs={3}>
                          <FormItem label="SSN">
                            <TextField
                              id="ssn"
                              name="ssn"
                              {...getFieldProps("ssn")}
                              value={values.ssn || ""}
                            />
                          </FormItem>
                        </StyledGridCol>
                        <StyledGridCol xs={4}>
                          <FormItem label="DL Number">
                            <StyledFlex gap="2px">
                              <TextField
                                id="dlNumber"
                                name="dlNumber"
                                {...getFieldProps("dlNumber")}
                                value={values.dlNumber || ""}
                              />
                            </StyledFlex>
                          </FormItem>
                        </StyledGridCol>
                        <StyledGridCol>
                          <FormItem label="DL State">
                            <StyledFlex gap="2px">
                              <div
                                style={{ maxWidth: "100px", minWidth: "80px" }}
                              >
                                <Select
                                  id="dlState"
                                  name="dlState"
                                  options={getOptionsFromRefList("USState")}
                                  placeholder="State"
                                  {...getFieldProps("dlState")}
                                  onChange={(value) =>
                                    setFieldValue("dlState", value)
                                  }
                                  value={values.dlState || ""}
                                />
                              </div>
                            </StyledFlex>
                          </FormItem>
                        </StyledGridCol>
                        <StyledGridCol xs={4}>
                          <FormItem label="DOB">
                            <CommonDatePicker
                              placeholder="MM/DD/YYYY"
                              format="MM/DD/YYYY"
                              showTime={false}
                              disableFutureDates={true}
                              inputProps={{ maxLength: 16 }}
                              id="dob"
                              name="dob"
                              value={values.dob ? dayjs(values.dob) : null}
                              onChange={(date, dateString) =>
                                setFieldValue("dob", dateString)
                              }
                            />
                          </FormItem>
                        </StyledGridCol>
                        <StyledGridCol xs={4}>
                          <FormItem label="SID">
                            <TextField
                              id="sid"
                              name="sid"
                              {...getFieldProps("sid")}
                              value={values.sid || ""}
                            />
                          </FormItem>
                        </StyledGridCol>
                        <StyledGridCol xs={4}>
                          <FormItem label="PIN">
                            <TextField
                              id="pinNo"
                              name="pinNo"
                              {...getFieldProps("pinNo")}
                              value={values.pinNo || ""}
                            />
                          </FormItem>
                        </StyledGridCol>
                        <StyledGridCol xs={4}>
                          <FormItem
                            label="Include Aliases?"
                            layout="horizontal"
                            labelPostion="label-right"
                          >
                            <Checkbox
                              id="includeAliases"
                              name="includeAliases"
                              {...getFieldProps("includeAliases")}
                              checked={values.includeAliases || false}
                              onChange={(e) =>
                                setFieldValue(
                                  "includeAliases",
                                  e.target.checked,
                                )
                              }
                            />
                          </FormItem>
                        </StyledGridCol>
                      </StyledGridRow>
                    </StyledGridCol>
                  </StyledGridRow>
                </StyledSearchWrapper>
              </form>
            );
          }}
        </Formik>
      </>
    );
  },
);

export default FolderDetailSearchForm;
